'use client';

import { Portal, Select } from '@chakra-ui/react';
import type { ListCollection, SelectRootProps } from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';

import type { FormBaseInputProps, FormValues } from '../../../types';
import { FormFloatingField } from '../../wrapper';

export type BaseItem = {
	category?: string
	label:     string
	value:     string
};
export type GroupedItem = {
	category: string
	label:    string
	value:    string
};

export type SelectInputGroupProps<T extends FormValues> = FormBaseInputProps<T>
	& Omit<SelectRootProps, 'collection' | 'multiple' | 'name' | 'onValueChange' | 'value'> & {
		clearTrigger?: boolean
		collection:    ListCollection<BaseItem>
		multiple?:     boolean
		placeholder?:  string
	};

function normalizeSelectValue(v: unknown): string[] {
	if (Array.isArray(v)) return v.map(x => (typeof x === 'string' ? x : String(x ?? '')));

	if (v == null || v === '') return [];

	if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return [ String(v) ];

	return [];
}

function toGroupedItems(items: BaseItem[]): GroupedItem[] {
	return items.map(i => ({
		category: i.category ?? '',
		label:    i.label,
		value:    i.value,
	}));
}

function groupByCategory<T extends GroupedItem>(items: T[]): Record<string, T[]> {
	return items.reduce<Record<string, T[]>>((acc, item) => {
		const k = item.category
		;(acc[k] ||= []).push(item);

		return acc;
	}, {});
}

export const SelectInputGroup = <T extends FormValues>({
	clearTrigger = false,
	collection,
	helperText,
	label,
	multiple,
	name,
	optionalText,
	placeholder = 'Select an option',
	required,
	...selectRootProps
}: SelectInputGroupProps<T>) => {
	const { control } = useFormContext<T>();

	const items = toGroupedItems(collection.items);
	const hasGroups = items.some(i => i.category !== '');
	const grouped = hasGroups ? groupByCategory(items) : null;
	const groups: Array<[string, GroupedItem[]]>
		= hasGroups && grouped ? Object.entries(grouped) : [ [ '', items ] ];

	return (
		<FormFloatingField
			helperText={helperText}
			label={label}
			name={name}
			optionalText={optionalText}
			required={required}
		>
			<Controller
				control={control}
				name={name}
				render={({ field }) => {
					const uiValue = normalizeSelectValue(field.value);

					const handleChange = ({ value }: { value: string[] }) => {
						if (multiple) field.onChange(value ?? []);
						else field.onChange(value?.[0] ?? '');
					};

					return (
						<Select.Root
							collection={collection}
							multiple={multiple}
							name={field.name}
							onInteractOutside={() => field.onBlur()}
							onValueChange={handleChange}
							value={uiValue}
							{...selectRootProps}
						>
							<Select.HiddenSelect />
							<Select.Control>
								<Select.Trigger>
									<Select.ValueText placeholder={placeholder} />
								</Select.Trigger>
								<Select.IndicatorGroup>
									{clearTrigger && <Select.ClearTrigger />}
									<Select.Indicator />
								</Select.IndicatorGroup>
							</Select.Control>
							<Portal>
								<Select.Positioner>
									<Select.Content>
										{groups.map(([ category, items ]) => (
											<Select.ItemGroup key={category || 'ungrouped'}>
												{category && (
													<Select.ItemGroupLabel>
														{category}
													</Select.ItemGroupLabel>
												)}
												{items.map((item) => {
													return (
														<Select.Item item={item} key={item.value} ml={category ? 4 : 0}>
															{item.label}
															<Select.ItemIndicator />
														</Select.Item>
													);
												})}
											</Select.ItemGroup>
										))}
									</Select.Content>
								</Select.Positioner>
							</Portal>
						</Select.Root>
					);
				}}
			/>
		</FormFloatingField>
	);
};
